<template>
    <div v-if="versionGuardAllowed && loadedLayoutRevisions" id="container">
        <div id="system-names">
            <div class="ds-section-title-medium section-title-dark pl-4">System names</div>
            <div id="system-name-links">
                <div @click="selectSystem(index)" :class="{'mode-links-selected': index === selectedSystemIndex}" class="mode-links system-name-container" v-for="(layoutRevision, index) in layoutRevisions" :key="index + '-key'">
                    <div class="system-name-text">
                        <a class="pl-4" :class="{'notification-text': layoutRevisionMismatch(index)}">{{layoutRevision.name}}</a>
                    </div>
                    <div v-if="layoutRevisionMismatch(index)" class="system-name-icon mr-4">
                        <div class="exclamation-icon">
                            <img src="@/assets/icons/exclamation.svg" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div id="system-info">
            <div class="ml-4" v-if="selectedSystemIndex !== undefined">
                <p class="ds-section-title-huge">{{layoutRevisions[selectedSystemIndex].name}}</p>
                <hr />
                <p class="ds-section-title-medium">Layout revisions</p>
                <table class="staticTable text-medium">
                    <thead>
                        <tr>
                            <th>
                                Source
                            </th>
                            <th :class="{'notification-text': layoutRevisionMismatch(selectedSystemIndex)}">
                                Revision
                            </th>
                            <th>
                                Date
                            </th>
                            <th>
                                Time
                            </th>
                            <th>
                                User
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>License</td>
                            <td :class="{'notification-text': layoutRevisionMismatch(selectedSystemIndex)}">{{licenseLayoutRevision}}</td>
                            <td>{{licenseCreationDate}}</td>
                            <td>{{licenseCreationTime}}</td>
                            <td>{{licenseEmail}}</td>
                        </tr>
                        <tr>
                            <td>FleetController</td>
                            <td :class="{'notification-text': layoutRevisionMismatch(selectedSystemIndex)}">{{systemLayoutRevision}}</td>
                            <td>N/A</td>
                            <td>N/A</td>
                            <td>N/A</td>
                        </tr>
                        <tr v-for="(agv, index) in agvs" :key="agv.agvId + index">
                            <td>{{'AGV' + agv.agvId}}</td>
                            <td :class="{'notification-text': layoutRevisionMismatch(selectedSystemIndex)}">{{agv.layoutRevision}}</td>
                            <td>{{parseDate(agv.layoutModificationTime)}}</td>
                            <td>{{parseTime(agv.layoutModificationTime)}}</td>
                            <td>N/A</td>
                        </tr>
                    </tbody>
                </table>
                <hr />
            </div>
        </div>
    </div>
    <div v-else-if="versionGuardAllowed && loadingLayoutRevisions">
        <el-icon class="is-loading" style="font-size: 100px;">
            <loading/>
        </el-icon>
    </div>
    <div v-else-if="versionGuardAllowed">
        <p class="ml-2">There was a problem loading layout revisions. Please try again later.</p>
    </div>
    <div v-else>
        <p class="ml-2">You do not have access rights to version guard feature.</p>
    </div>
</template>

<style lang="scss" scoped>
// Notification/red-brick
$--color-notification: #BF5757;

#container {
    display: flex;
}

#system-names {
    flex: 0 1 auto;
    width: 230px;
}

#system-name-links {
    overflow-y: auto;
    background-color: $--color-graphite;
    height: calc(100vh - 2 * #{$--navigation-height}); // navigation bar + system names
}

#system-info {
    flex: 1 0 auto;
    overflow-y: auto;
    max-height: calc(100vh - #{$--navigation-height});
}

// Make horizontal line to stop after content
#system-info > div {
    display: inline-block;
}

.system-name-container {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.system-name-text {
    flex: 1 1 auto;
}

.system-name-icon {
    flex: 0 0 auto;
    display: inline-block;
}

.exclamation-icon {
    background-color: $--color-notification;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.exclamation-icon img {
    width: 80%;
    height: 80%;
}

.notification-text {
    color: $--color-notification;
}

.section-title-dark {
    color: $--color-text-chrome;
    background-color: $--color-text-monument-light;
    height: $--navigation-height;
    line-height: $--navigation-height;
}

.ds-section-title-large {
    font-size: $--font-size-extra-large;
}

th, td {
    padding-right: 2rem;
    line-height: 2;
    letter-spacing: 1px;
}

th {
    text-align: left;
}

table {
    border-collapse: collapse;
}

tr:hover {
    background-color: $--color-platinum;
}

hr {
    border: 1px solid $--color-cloud;
    border-bottom-width: 0px;
}

</style>

<script lang='ts'>
import { QueryLayoutRevisions } from '@/store/layout/actions';
import { AgvLayout } from '@/store/layout/types';
import { parseTime, parseDate } from '@/util/utils';
import { ElMessage } from 'element-plus';
import { Loading } from '@element-plus/icons-vue';
import { trackError } from '@/util/telemetry';

// Text to shown when information isn't available.
const NA = 'N/A';

import { defineComponent } from 'vue';

export default defineComponent({
    name: 'VersionGuard',
    mounted() {
        if ((this.$store.state.layout.layoutRevisions as any[]).length === 0) {
            this.queryLayoutRevisions();
        } else {
            this.loadedLayoutRevisions = true;
            this.selectedSystemIndex = 0;
        }
    },
    data() {
        return {
            versionGuardAllowed: this.$store.state.user.versionGuardAllowed,
            loadingLayoutRevisions: true,
            loadedLayoutRevisions: false,
            selectedSystemIndex: undefined as undefined | number,
        };
    },
    methods: {
        async queryLayoutRevisions() {
            try {
                await this.$store.dispatch(new QueryLayoutRevisions());
                this.loadedLayoutRevisions = true;
            } catch (error) {
                ElMessage({
                    message: 'Could not get layout revisions: ' + error,
                    type: 'error',
                    showClose: true,
                    duration: 0});
                trackError('Could not get layout revisions: ' + String(error), 'LP-ERR-FE-006', 'queryLayoutRevisions');
            }
            this.loadingLayoutRevisions = false;
            if (this.layoutRevisions.length > 0) {
                this.selectedSystemIndex = 0;
            }
        },
        selectSystem(index: number) {
            this.selectedSystemIndex = index;
        },
        // Check if there's a mismatch between selected system's layout revisions.
        // If there are no AGV layouts it's considered a mismatch.
        layoutRevisionMismatch(i: number): boolean {
            const agvInfo: AgvLayout[] = this.layoutRevisions[i]?.agv ?? [];
            const agvLayouts = agvInfo.map((a) => a.layoutRevision);
            const licenseLayout = this.layoutRevisions[i]?.license?.layoutRevision;
            const systemLayout = this.layoutRevisions[i]?.system?.layoutRevision;
            return !(agvLayouts.every((a) => licenseLayout === a) && agvLayouts.length > 0
                    && licenseLayout === systemLayout && licenseLayout !== null &&
                    licenseLayout !== undefined);
        },
        parseTime(s: string | null | undefined) {
            return parseTime(s) ?? NA;
        },
        parseDate(s: string | null | undefined) {
            return parseDate(s) ?? NA;
        },
    },
    computed: {
        layoutRevisions() {
            return this.$store.state.layout.layoutRevisions;
        },
        licenseLayoutRevision(): string {
            const i = this.selectedSystemIndex;
            return (i !== undefined ? this.layoutRevisions[i]?.license?.
                    layoutRevision ?? NA : NA);
        },
        licenseCreationDate(): string {
            const i = this.selectedSystemIndex;
            return parseDate(i !== undefined ? this.layoutRevisions[i]?.license?.
                    creationTimeStamp : undefined) ?? NA;
        },
        licenseCreationTime(): string {
            const i = this.selectedSystemIndex;
            return parseTime (i !== undefined ? this.layoutRevisions[i]?.license?.
                    creationTimeStamp : undefined) ?? NA;
        },
        licenseEmail(): string {
            const i = this.selectedSystemIndex;
            return (i !== undefined ? this.layoutRevisions[i]?.license?.
                    email ?? NA : NA);
        },
        systemLayoutRevision(): string {
            const i = this.selectedSystemIndex;
            return (i !== undefined ? this.layoutRevisions[i]?.system?.
                    layoutRevision ?? NA : NA);
        },
        agvs(): AgvLayout[] {
            const i = this.selectedSystemIndex;
            return i !== undefined ? this.layoutRevisions[i]?.agv ?? [] : [];
        },
        licenseTime(): string {
            const i = this.selectedSystemIndex;
            return parseTime(i !== undefined ? this.layoutRevisions[i]?.license?.creationTimeStamp : undefined) ?? NA;
        },
        licenseDate(): string {
            const i = this.selectedSystemIndex;
            return parseDate(i !== undefined ? this.layoutRevisions[i]?.license?.creationTimeStamp : undefined) ?? NA;
        },
    },
    components: {
        Loading,
    },
});
</script>
